import type {
  BlogCategory,
  BlogGameRef,
  BlogPost,
  BlogTag,
} from '../../../shared/models/blog.model';
import { joinImagesUrl } from '../../../shared/utils/home-blocks.utils';
import type { BlogChipApi, BlogGameApi, BlogPostCardApi } from './blog.api-model';

// Tarjeta pública del blog (B79) → `BlogPost`. Módulo aparte de
// `blog.mappers.ts` (P48): la landing de juego la usa desde
// `game-section.mappers.ts`, que va en el bundle inicial por la cabecera, y
// así solo viaja esto y no la ficha, el listado ni las taxonomías.

/** Color de respaldo de un juego sin color propio (`GAME_FALLBACK_ACCENT_COLOR`). */
export const BLOG_FALLBACK_ACCENT_COLOR = '#385898';

/**
 * El juego «General» (B29): espejo de `BlogPost::GAME_FALLBACK` de la API, que
 * lo emite cuando la entrada no tiene sección publicada. La API resuelve
 * `?game=general` con esos mismos casos (B84), así que también es la última
 * pastilla de la barra de filtros (P42).
 */
export const BLOG_GENERAL_GAME: BlogGameRef = {
  id: 'general',
  name: 'General',
  slug: 'general',
  accentColor: BLOG_FALLBACK_ACCENT_COLOR,
};

/**
 * Booleano defensivo: la API de la casa ha llegado a emitir booleanos como
 * `0/1` o como cadena según el driver. `!!` no sirve: `!!'0'` es `true`.
 */
function toBool(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/** Ruta de imagen de la API → URL absoluta (`''` si no hay). */
export function blogImageUrl(path: string | null | undefined): string {
  return path ? joinImagesUrl(path) : '';
}

/** Chip de taxonomía (categoría, etiqueta o categoría de tienda) → vista. */
export function toBlogChip(api: BlogChipApi): BlogCategory & BlogTag {
  return { id: String(api.id), name: api.name, slug: api.slug };
}

/**
 * `game` nunca es `null` en el contrato (B29). Si aun así faltara o llegara
 * sin slug, se cae al respaldo en vez de romper las plantillas que lo pintan
 * (el acento de la ficha, las migas, el badge de las tarjetas).
 */
export function toBlogGameRef(api: BlogGameApi | null | undefined): BlogGameRef {
  if (!api?.slug) return BLOG_GENERAL_GAME;
  return {
    id: String(api.id),
    name: api.name,
    slug: api.slug,
    accentColor: api.accent_color || BLOG_FALLBACK_ACCENT_COLOR,
  };
}

/**
 * Tarjeta (B79). La tarjeta pública no trae autor: los tres campos van vacíos
 * y las plantillas de tarjeta no pintan la fila (B125, P41).
 */
export function toBlogPostCard(api: BlogPostCardApi): BlogPost {
  return {
    id: String(api.id),
    title: api.title,
    slug: api.slug,
    excerpt: api.excerpt,
    imageUrl: blogImageUrl(api.image_url),
    imageAlt: api.image_alt ?? '',
    imageWidth: Number(api.image_width),
    imageHeight: Number(api.image_height),
    game: toBlogGameRef(api.game),
    categories: (api.categories ?? []).map(toBlogChip),
    tags: (api.tags ?? []).map(toBlogChip),
    relatedProducts: [],
    publishedAt: api.published_at,
    readTimeMinutes: Number(api.read_time_minutes),
    featured: toBool(api.featured),
    authorName: '',
    authorRole: '',
    authorAvatarUrl: '',
  };
}
